import { CAMERAS, type Camera } from './footage';
import { isRecord, parseJson, text } from './json';
import { parseLocalTime } from './time';

export const NOTE_KINDS = ['damage', 'approach', 'change', 'near_miss', 'emergency', 'animal'] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];

export const VERDICTS = ['incident', 'notable', 'routine', 'false_alarm'] as const;
export type Verdict = (typeof VERDICTS)[number];

export const SUBJECTS = ['adult', 'child', 'vehicle', 'cyclist', 'animal', 'unknown'] as const;
export type Subject = (typeof SUBJECTS)[number];

export const DISTANCES = ['touching', 'under_1m', '1_3m', 'over_3m'] as const;
export type Distance = (typeof DISTANCES)[number];

export interface AiMoment {
  readonly kind: NoteKind;
  readonly start: number;
  readonly end: number;
  readonly peak: number;
  readonly camera: Camera | undefined;
  readonly subject: Subject | undefined;
  readonly subjectId: string;
  readonly distance: Distance | undefined;
  readonly plate: string;
  readonly label: string;
  readonly confidence: number | undefined;
}

const CONTACT_CONFIDENCE = 0.7;

export const unsureContact = (moment: AiMoment): boolean =>
  (moment.kind === 'damage' || (moment.kind === 'approach' && moment.distance === 'touching')) && (moment.confidence ?? 0) < CONTACT_CONFIDENCE;

export interface AiNotes {
  readonly version: number | undefined;
  readonly model: string;
  readonly summary: string;
  readonly verdict: Verdict | undefined;
  readonly verdictReason: string;
  readonly tags: readonly string[];
  readonly moments: readonly AiMoment[];
}

const SUBJECT_NAMES: Readonly<Record<Subject, string>> = {
  adult: 'Person',
  child: 'Child',
  vehicle: 'Vehicle',
  cyclist: 'Cyclist',
  animal: 'Animal',
  unknown: 'Someone',
};
const NEARNESS: Readonly<Record<Distance, string>> = {
  touching: 'touched the car',
  under_1m: 'close',
  '1_3m': 'nearby',
  over_3m: 'nearby',
};
const KIND_TITLES: Readonly<Record<Exclude<NoteKind, 'approach'>, string>> = {
  damage: 'Damage',
  change: 'Scene change',
  near_miss: 'Near miss',
  emergency: 'Emergency vehicle',
  animal: 'Animal',
};

export const VERDICT_TITLES: Readonly<Record<Verdict, string>> = {
  incident: 'Incident',
  notable: 'Worth a look',
  routine: 'Routine',
  false_alarm: 'Likely false alarm',
};

export const momentTitle = (moment: AiMoment): string =>
  unsureContact(moment)
    ? 'Possible contact'
    : moment.kind === 'approach' ? `${SUBJECT_NAMES[moment.subject ?? 'unknown']} ${NEARNESS[moment.distance ?? 'over_3m']}` : KIND_TITLES[moment.kind];

const oneOf = <T extends string>(values: readonly T[], value: unknown): T | undefined => values.find((candidate) => candidate === text(value));

const timeOf = (value: unknown): number | undefined => parseLocalTime(text(value));

const confidenceOf = (value: unknown): number | undefined => (typeof value === 'number' && value >= 0 && value <= 1 ? value : undefined);

function parseMoment(value: unknown): AiMoment | undefined {
  if (!isRecord(value)) return undefined;
  const kind = oneOf(NOTE_KINDS, value['kind']);
  const start = timeOf(value['start']);
  if (kind === undefined || start === undefined) return undefined;
  const end = Math.max(start, timeOf(value['end']) ?? start);
  const peak = timeOf(value['peak']) ?? start;
  const moment = {
    kind,
    start,
    end,
    peak: Math.min(end, Math.max(start, peak)),
    camera: oneOf(CAMERAS, value['camera']),
    subject: oneOf(SUBJECTS, value['subject']),
    subjectId: text(value['subject_id']),
    distance: oneOf(DISTANCES, value['distance']),
    plate: text(value['plate']),
    label: text(value['label']),
    confidence: confidenceOf(value['confidence']),
  };
  return { ...moment, label: moment.label || momentTitle(moment) };
}

const versionOf = (value: unknown): number | undefined => (Number.isInteger(value) && Number(value) > 0 ? Number(value) : undefined);

const listOf = (value: unknown): readonly unknown[] => (Array.isArray(value) ? value : []);

export function parseAiNotes(json: string): AiNotes | undefined {
  const parsed = parseJson(json);
  if (!isRecord(parsed)) return undefined;
  return {
    version: versionOf(parsed['version']),
    model: text(parsed['model']),
    summary: text(parsed['summary']),
    verdict: oneOf(VERDICTS, parsed['verdict']),
    verdictReason: text(parsed['verdict_reason']),
    tags: [...new Set(listOf(parsed['tags']).map((tag) => text(tag).toLowerCase()).filter(Boolean))],
    moments: listOf(parsed['moments'])
      .flatMap((moment) => parseMoment(moment) ?? [])
      .sort((a, b) => a.start - b.start),
  };
}
